/**
 * Pure presentation logic for the platform reporting dashboard (PE-2108).
 *
 * Extracted from PlatformReportingPage so the money formatting can be tested
 * without mounting React. That formatter is the important one: EVERY money
 * field on this response is minor units (kobo), including `orders.totalRevenue`
 * and `perMerchant.gmv`, whose names omit the `Minor` suffix. Formatting one of
 * those as if it were naira renders it 100x too large — a bug this codebase has
 * already shipped once. See ./types.ts for the field-by-field provenance.
 */
import type { PlatformOrdersPerMerchant } from './types';

/** Plain integer formatter — for counts, never money. */
export const fmtCount = new Intl.NumberFormat('en-NG');

/**
 * Formats a minor-unit (kobo) amount as currency.
 *
 * Honours the response's own `currency` rather than hardcoding NGN, and drops
 * the decimals because these are estate-wide totals.
 */
export function fmtMinor(valueMinor: number, currency: string): string {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency || 'NGN',
    maximumFractionDigits: 0,
  }).format(valueMinor / 100);
}

/** Share of `total` that `count` represents, as a whole percent. */
export function statusPercent(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

/** True when the user has picked a from-date later than the to-date. */
export function isRangeInvalid(from: string, to: string): boolean {
  return Boolean(from && to && new Date(from) > new Date(to));
}

/** The ten highest-GMV merchants, highest first. Does not mutate `rows`. */
export function topMerchantsByGmv(rows: PlatformOrdersPerMerchant[]): PlatformOrdersPerMerchant[] {
  return [...rows].sort((a, b) => b.gmv - a.gmv).slice(0, 10);
}
